/**
 * Project Lists Page — Web Component
 * Trade-only page for managing project lists stored in localStorage.
 * Reads/writes `ab-project-lists` key (same as PDP "Add to Project List" button).
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

const STORAGE_KEY = 'ab-project-lists';

class ABProjectListsPage extends ABBaseComponent {
  connectedCallback() {
    this.mode = 'overview'; // 'overview' | 'detail'
    this.activeListId = null;

    // Check trade status
    if (!window.ABTrade?.isTradeCustomer) {
      this.renderTradeGate();
      return;
    }

    this.render();
  }

  // --- Data helpers ---

  getLists() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  saveLists(lists) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
  }

  getListById(id) {
    return this.getLists().find(l => l.id === id) || null;
  }

  generateId() {
    return `pl_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }

  formatPrice(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  showToast(message) {
    const existing = document.querySelector('.ab-project-lists-page__toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'ab-project-lists-page__toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  }

  // --- Rendering ---

  render() {
    if (this.mode === 'detail' && this.activeListId) {
      this.renderDetail();
    } else {
      this.renderOverview();
    }
  }

  renderTradeGate() {
    const heading = this.dataset.tradeInfoHeading || 'Trade Professional Feature';
    const text = this.dataset.tradeInfoText || 'Project Lists are an exclusive feature for our trade professionals. Create and manage project lists, track pricing, and easily reorder for your projects.';
    const ctaUrl = this.dataset.tradeCtaUrl || '/pages/trade-professional-program';

    this.innerHTML = `
      <h1 class="ab-project-lists-page__heading">${this.dataset.heading || 'Project Lists'}</h1>
      <div class="ab-project-lists-page__trade-gate">
        <h2>${this.escHtml(heading)}</h2>
        <p>${this.escHtml(text)}</p>
        <a href="${ctaUrl}" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary">Learn About Trade Program</a>
      </div>
    `;
  }

  renderOverview() {
    const lists = this.getLists();
    const heading = this.dataset.heading || 'Project Lists';

    let cardsHtml = '';
    if (lists.length === 0) {
      cardsHtml = `
        <div class="ab-project-lists-page__empty">
          <p>You don't have any project lists yet. Create one to start organizing products for your projects.</p>
          <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary" data-action="create">
            Create Your First List
          </button>
        </div>
      `;
    } else {
      cardsHtml = '<div class="ab-project-lists-page__grid">';
      for (const list of lists) {
        const items = list.items || [];
        const tradeTotal = items.reduce((sum, i) => sum + (i.trade_price || 0) * (i.quantity || 1), 0);
        cardsHtml += `
          <div class="ab-project-lists-page__card" data-action="open" data-list-id="${list.id}">
            <div class="ab-project-lists-page__card-name">${this.escHtml(list.name || 'Untitled')}</div>
            <p class="ab-project-lists-page__card-meta">${items.length} item${items.length !== 1 ? 's' : ''} &middot; Trade Total: ${this.formatPrice(tradeTotal)}</p>
            <p class="ab-project-lists-page__card-meta">Updated ${this.formatDate(list.updated_at)}</p>
            <div class="ab-project-lists-page__card-actions">
              <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--outline ab-project-lists-page__btn--sm" data-action="rename" data-list-id="${list.id}">Rename</button>
              <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--danger ab-project-lists-page__btn--sm" data-action="delete" data-list-id="${list.id}">Delete</button>
            </div>
          </div>
        `;
      }
      cardsHtml += '</div>';
    }

    this.innerHTML = `
      <h1 class="ab-project-lists-page__heading">${this.escHtml(heading)}</h1>
      ${lists.length > 0 ? `
        <div class="ab-project-lists-page__toolbar">
          <span class="ab-project-lists-page__count">${lists.length} list${lists.length !== 1 ? 's' : ''}</span>
          <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary ab-project-lists-page__btn--sm" data-action="create">+ New List</button>
        </div>
      ` : ''}
      ${cardsHtml}
    `;

    this.bindOverviewEvents();
  }

  renderDetail() {
    const list = this.getListById(this.activeListId);
    if (!list) {
      this.mode = 'overview';
      this.renderOverview();
      return;
    }

    const items = list.items || [];
    const msrpTotal = items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0);
    const tradeTotal = items.reduce((sum, i) => sum + (i.trade_price || 0) * (i.quantity || 1), 0);
    const savings = msrpTotal - tradeTotal;

    let tableHtml = '';
    if (items.length === 0) {
      tableHtml = `
        <div class="ab-project-lists-page__empty">
          <p>This list is empty. Browse products and use the "Add to Project List" button on product pages.</p>
          <a href="/collections/all" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary">Browse Products</a>
        </div>
      `;
    } else {
      tableHtml = `
        <table class="ab-project-lists-page__table">
          <thead>
            <tr>
              <th></th>
              <th>Product</th>
              <th>Unit Price</th>
              <th>Qty</th>
              <th>Line Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
      `;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const lineTotal = (item.trade_price || 0) * (item.quantity || 1);
        tableHtml += `
          <tr>
            <td>
              ${item.image
                ? `<img src="${item.image}" alt="${this.escHtml(item.title)}" class="ab-project-lists-page__item-image" loading="lazy">`
                : '<div class="ab-project-lists-page__item-image"></div>'
              }
            </td>
            <td>
              <a href="${item.url || '#'}" class="ab-project-lists-page__item-title">${this.escHtml(item.title || 'Product')}</a>
              ${item.variant_title ? `<div class="ab-project-lists-page__item-variant">${this.escHtml(item.variant_title)}</div>` : ''}
            </td>
            <td>${this.formatPrice(item.trade_price || 0)}</td>
            <td>
              <input type="number" min="1" max="99" value="${item.quantity || 1}"
                class="ab-project-lists-page__qty-input"
                data-action="qty" data-item-index="${i}">
            </td>
            <td>${this.formatPrice(lineTotal)}</td>
            <td>
              <button type="button" class="ab-project-lists-page__remove-btn" data-action="remove-item" data-item-index="${i}" aria-label="Remove item" title="Remove">&times;</button>
            </td>
          </tr>
        `;
      }
      tableHtml += '</tbody></table>';
    }

    const summaryHtml = items.length > 0 ? `
      <div class="ab-project-lists-page__summary">
        <div class="ab-project-lists-page__summary-row">
          <span>MSRP Total</span>
          <span>${this.formatPrice(msrpTotal)}</span>
        </div>
        <div class="ab-project-lists-page__summary-row ab-project-lists-page__summary-row--savings">
          <span>Trade Savings</span>
          <span>-${this.formatPrice(savings)}</span>
        </div>
        <div class="ab-project-lists-page__summary-row ab-project-lists-page__summary-row--total">
          <span>Trade Total</span>
          <span>${this.formatPrice(tradeTotal)}</span>
        </div>
      </div>
    ` : '';

    const actionsHtml = items.length > 0 ? `
      <div class="ab-project-lists-page__detail-actions">
        <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary" data-action="add-all-to-cart">Add All to Cart</button>
        <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--secondary" data-action="email-quote">Email Quote</button>
        <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--danger" data-action="delete-list">Delete List</button>
      </div>
    ` : `
      <div class="ab-project-lists-page__detail-actions">
        <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--danger" data-action="delete-list">Delete List</button>
      </div>
    `;

    this.innerHTML = `
      <div class="ab-project-lists-page__detail-header">
        <button type="button" class="ab-project-lists-page__back-btn" data-action="back">&larr; All Lists</button>
        <h2 class="ab-project-lists-page__detail-name">${this.escHtml(list.name || 'Untitled')}</h2>
        <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--outline ab-project-lists-page__btn--sm" data-action="rename-detail">Rename</button>
      </div>
      ${tableHtml}
      ${summaryHtml}
      ${actionsHtml}
    `;

    this.bindDetailEvents();
  }

  // --- Event binding ---

  bindOverviewEvents() {
    this.addEventListener('click', this._handleOverviewClick = (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      const listId = target.dataset.listId;

      switch (action) {
        case 'open':
          e.preventDefault();
          this.activeListId = listId;
          this.mode = 'detail';
          this.render();
          break;
        case 'create':
          e.preventDefault();
          e.stopPropagation();
          this.showCreateDialog();
          break;
        case 'rename':
          e.preventDefault();
          e.stopPropagation();
          this.renameList(listId);
          break;
        case 'delete':
          e.preventDefault();
          e.stopPropagation();
          this.deleteList(listId);
          break;
      }
    });
  }

  bindDetailEvents() {
    // Remove old listener
    if (this._handleOverviewClick) {
      this.removeEventListener('click', this._handleOverviewClick);
    }

    this.addEventListener('click', this._handleDetailClick = (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      switch (action) {
        case 'back':
          this.mode = 'overview';
          this.activeListId = null;
          this.removeEventListener('click', this._handleDetailClick);
          this.render();
          break;
        case 'rename-detail':
          this.renameList(this.activeListId);
          break;
        case 'remove-item':
          this.removeItem(parseInt(target.dataset.itemIndex, 10));
          break;
        case 'add-all-to-cart':
          this.addAllToCart();
          break;
        case 'email-quote':
          this.emailQuote();
          break;
        case 'delete-list':
          this.deleteList(this.activeListId, true);
          break;
      }
    });

    // Quantity change
    this.querySelectorAll('[data-action="qty"]').forEach(input => {
      input.addEventListener('change', (e) => {
        const idx = parseInt(e.target.dataset.itemIndex, 10);
        const newQty = Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1));
        e.target.value = newQty;
        this.updateItemQty(idx, newQty);
      });
    });
  }

  // --- Actions ---

  showCreateDialog() {
    const overlay = document.createElement('div');
    overlay.className = 'ab-project-lists-page__dialog';
    overlay.innerHTML = `
      <div class="ab-project-lists-page__dialog-box">
        <h3>Create New List</h3>
        <input type="text" class="ab-project-lists-page__dialog-input" placeholder="e.g. Smith Residence Master Bath" autofocus maxlength="100">
        <div class="ab-project-lists-page__dialog-actions">
          <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--outline ab-project-lists-page__btn--sm" data-dialog="cancel">Cancel</button>
          <button type="button" class="ab-project-lists-page__btn ab-project-lists-page__btn--primary ab-project-lists-page__btn--sm" data-dialog="confirm">Create</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    const input = overlay.querySelector('input');
    const confirm = () => {
      const name = input.value.trim();
      if (!name) {
        input.focus();
        return;
      }
      const lists = this.getLists();
      lists.push({
        id: this.generateId(),
        name,
        items: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      this.saveLists(lists);
      overlay.remove();
      this.showToast(`Created "${name}"`);
      this.render();
    };

    overlay.querySelector('[data-dialog="confirm"]').addEventListener('click', confirm);
    overlay.querySelector('[data-dialog="cancel"]').addEventListener('click', () => overlay.remove());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') confirm();
      if (e.key === 'Escape') overlay.remove();
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });

    setTimeout(() => input.focus(), 50);
  }

  renameList(listId) {
    const lists = this.getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return;

    const newName = prompt('Rename list:', list.name);
    if (newName === null || !newName.trim()) return;

    list.name = newName.trim();
    list.updated_at = new Date().toISOString();
    this.saveLists(lists);
    this.showToast(`Renamed to "${list.name}"`);
    this.render();
  }

  deleteList(listId, returnToOverview = false) {
    const lists = this.getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return;

    if (!confirm(`Delete "${list.name}"? This cannot be undone.`)) return;

    const filtered = lists.filter(l => l.id !== listId);
    this.saveLists(filtered);
    this.showToast(`Deleted "${list.name}"`);

    if (returnToOverview || this.mode === 'overview') {
      this.mode = 'overview';
      this.activeListId = null;
    }
    this.render();
  }

  removeItem(index) {
    const lists = this.getLists();
    const list = lists.find(l => l.id === this.activeListId);
    if (!list || !list.items || !list.items[index]) return;

    const itemName = list.items[index].title;
    list.items.splice(index, 1);
    list.updated_at = new Date().toISOString();
    this.saveLists(lists);
    this.showToast(`Removed "${itemName}"`);
    this.render();
  }

  updateItemQty(index, qty) {
    const lists = this.getLists();
    const list = lists.find(l => l.id === this.activeListId);
    if (!list || !list.items || !list.items[index]) return;

    list.items[index].quantity = qty;
    list.updated_at = new Date().toISOString();
    this.saveLists(lists);

    // Re-render summary without full re-render to avoid losing focus
    const items = list.items;
    const msrpTotal = items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0);
    const tradeTotal = items.reduce((sum, i) => sum + (i.trade_price || 0) * (i.quantity || 1), 0);
    const savings = msrpTotal - tradeTotal;

    // Update line total for this row
    const rows = this.querySelectorAll('.ab-project-lists-page__table tbody tr');
    if (rows[index]) {
      const cells = rows[index].querySelectorAll('td');
      if (cells[4]) cells[4].textContent = this.formatPrice((items[index].trade_price || 0) * qty);
    }

    // Update summary
    const summary = this.$('.ab-project-lists-page__summary');
    if (summary) {
      const summaryRows = summary.querySelectorAll('.ab-project-lists-page__summary-row');
      if (summaryRows[0]) summaryRows[0].querySelector('span:last-child').textContent = this.formatPrice(msrpTotal);
      if (summaryRows[1]) summaryRows[1].querySelector('span:last-child').textContent = `-${this.formatPrice(savings)}`;
      if (summaryRows[2]) summaryRows[2].querySelector('span:last-child').textContent = this.formatPrice(tradeTotal);
    }
  }

  async addAllToCart() {
    const list = this.getListById(this.activeListId);
    if (!list || !list.items || list.items.length === 0) return;

    const btn = this.$('[data-action="add-all-to-cart"]');
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    try {
      const cartItems = list.items.map(item => ({
        id: parseInt(item.variant_id, 10),
        quantity: item.quantity || 1
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        throw new Error(`Cart error: ${response.status}`);
      }

      btn.textContent = 'Added!';
      this.showToast(`${list.items.length} item${list.items.length !== 1 ? 's' : ''} added to cart`);

      // Dispatch event for cart drawer / header cart count
      document.dispatchEvent(new CustomEvent('cart:updated'));

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        // Offer redirect to cart
        if (confirm('Items added to cart. Go to cart now?')) {
          window.location.href = '/cart';
        }
      }, 800);
    } catch (error) {
      console.error('Add all to cart error:', error);
      btn.textContent = 'Error — Try Again';
      btn.disabled = false;
      this.showToast('Failed to add items to cart. Please try again.');
      setTimeout(() => { btn.textContent = originalText; }, 2000);
    }
  }

  emailQuote() {
    const list = this.getListById(this.activeListId);
    if (!list || !list.items || list.items.length === 0) return;

    const items = list.items;
    const tradeTotal = items.reduce((sum, i) => sum + (i.trade_price || 0) * (i.quantity || 1), 0);

    const subject = `Quote Request: ${list.name}`;
    const bodyLines = [
      `Project: ${list.name}`,
      `Date: ${new Date().toLocaleDateString()}`,
      '',
      'Items:',
      '---',
    ];

    items.forEach((item, idx) => {
      const lineTotal = (item.trade_price || 0) * (item.quantity || 1);
      bodyLines.push(`${idx + 1}. ${item.title}${item.variant_title ? ` - ${item.variant_title}` : ''}`);
      bodyLines.push(`   Qty: ${item.quantity || 1} x ${this.formatPrice(item.trade_price || 0)} = ${this.formatPrice(lineTotal)}`);
      bodyLines.push(`   ${window.location.origin}${item.url || ''}`);
      bodyLines.push('');
    });

    bodyLines.push('---');
    bodyLines.push(`Trade Total: ${this.formatPrice(tradeTotal)}`);
    bodyLines.push('');
    bodyLines.push('Please provide a formal quote for this project list.');

    const body = bodyLines.join('\n');
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  // --- Utilities ---

  escHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

defineComponent('ab-project-lists-page', ABProjectListsPage);
